<script setup lang="ts">
const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const props = withDefaults(
  defineProps<{
    to?: any
    variant?:
      | 'primary'
      | 'base'
      | 'gray'
      | 'simple'
      | 'orange'
      | 'outline'
      | undefined
    type?: 'button' | 'submit' | 'reset' | undefined
    size?: 'small' | 'normal' | undefined
    disabled?: boolean | undefined
    danger?: boolean
    loading?: boolean
    icon?: string
    iconPlacement?: 'left' | 'right'
    permission?: string
  }>(),
  {
    variant: 'base',
    type: 'button',
    loading: false,
  },
)

const store = useMainStore()

const hasPermission = computed(() => {
  if (!props.permission) return true
  return store.auth?.user?.permissions?.some(
    (p) =>
      p.startsWith(`${props.permission}:`) ||
      p === props.permission ||
      p === '**:w',
  )
})

const variantClasses: any = {
  primary: `px-4 border-2 text-white shadow-sm ${
    props.danger
      ? 'bg-red-600 border-red-600 hover:bg-red-700 hover:border-red-700 disabled:hover:bg-red-600 focus:ring-red-500'
      : 'bg-primary-600 border-primary-600 hover:bg-primary-700 hover:border-primary-700 disabled:hover:bg-primary-600 focus:ring-primary-500'
  }`,
  base: 'px-4 border text-gray-700 bg-white border-gray-300 hover:bg-gray-50 focus:ring-primary-500 shadow-sm',
  gray: 'px-4 border text-gray-700 bg-gray-100 border-gray-100 hover:bg-gray-200 hover:border-gray-200 focus:ring-primary-500',
  simple:
    'px-2 bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:ring-0 focus:ring-offset-0 focus:ring-primary-500',
  orange:
    'px-4 border text-gray-700 bg-orange-100 border-orange-100 hover:bg-orange-200 hover:border-orange-200 focus:ring-primary-500',
  outline:
    'px-4 border-2 text-primary-700 bg-white border-primary-200 hover:bg-primary-50 hover:border-primary-300 focus:ring-primary-500',
}

const classes = computed(() => [
  'flex justify-center items-center font-medium py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 gap-x-2',
  variantClasses[props.variant],
  props.size === 'small' ? 'text-xs py-1' : 'text-sm py-2',
  props.iconPlacement === 'right' ? 'flex-row-reverse' : '',
  props.loading || props.disabled || !hasPermission.value
    ? 'opacity-50 cursor-not-allowed'
    : '',
])

const isDisabled = computed(
  () => props.loading || props.disabled || !hasPermission.value,
)

function handleClick(event: MouseEvent) {
  if (isDisabled.value) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  emit('click', event)
}
</script>

<template>
  <NuxtLink
    v-if="to && !isDisabled"
    :to="to"
    :class="classes"
    :type="type"
    :disabled="loading || disabled"
    @click="handleClick"
  >
    <div
      v-if="loading || icon"
      :class="[
        loading ? 'animate-spin i-icon-park-outline:loading-four' : icon,
      ]"
    />
    <div
      v-if="$slots.default"
      class="flex justify-center items-center space-x-1 flex-grow"
    >
      <slot :loading="loading" :disabled="loading || disabled" />
    </div>
  </NuxtLink>
  <component
    v-else
    :is="to && !isDisabled ? 'NuxtLink' : 'div'"
    v-if="isDisabled"
    :to="!isDisabled ? to : undefined"
    :class="classes"
    :type="type"
    :aria-disabled="isDisabled"
  >
    <div
      v-if="loading || icon"
      :class="[
        loading ? 'animate-spin i-icon-park-outline:loading-four' : icon,
      ]"
    />
    <div
      v-if="$slots.default"
      class="flex justify-center items-center space-x-1 flex-grow"
    >
      <slot :loading="loading" :disabled="isDisabled" />
    </div>
  </component>
  <button
    v-else
    :class="classes"
    :type="type"
    :disabled="isDisabled"
    :aria-disabled="isDisabled"
    @click="handleClick"
  >
    <div
      v-if="loading || icon"
      :class="[
        loading ? 'animate-spin i-icon-park-outline:loading-four' : icon,
      ]"
    />
    <div
      v-if="$slots.default"
      class="flex justify-center items-center gap-x-1 flex-grow"
    >
      <slot :loading="loading" :disabled="isDisabled" />
    </div>
  </button>
</template>
